/* import __COLOCATED_TEMPLATE__ from './qa-status.hbs'; */
/* RESPONSIBLE TEAM: team-fin-qa */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import type Snackbar from 'embercom/services/snackbar';
import type Session from 'embercom/services/session';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import { putRequest, ResponseError } from 'embercom/lib/inbox/requests';
import {
  getValueFromMonitorPairs,
  setValueForMonitorPairs,
  statusMatchesFilter,
} from 'embercom/lib/inbox2/qa-monitor-helpers';
import generateUUID from 'embercom/lib/uuid-generator';
import QaStatusSelectItem from './qa-status-select-item';
import QaStatusSeparator from './qa-status-separator';

export enum QaStatusValue {
  Unreviewed = 'unreviewed',
  NeedsFix = 'needs_fix',
  WontFix = 'wont_fix',
  FixComplete = 'fix_complete',
  PassedReview = 'passed_review',
}

interface Signature {
  Args: {
    conversationEntry: ConversationTableEntry;
    statusValue?: string;
  };
}

type StatusIconType = 'dot' | 'check' | 'alert';

const STATUS_ICON_TYPES: Record<QaStatusValue, StatusIconType> = {
  [QaStatusValue.Unreviewed]: 'dot',
  [QaStatusValue.NeedsFix]: 'alert',
  [QaStatusValue.WontFix]: 'check',
  [QaStatusValue.FixComplete]: 'check',
  [QaStatusValue.PassedReview]: 'check',
};

interface SelectItem {
  text: string;
  value: QaStatusValue | '__separator__';
  component: typeof QaStatusSelectItem | typeof QaStatusSeparator;
  componentAttrs?: { iconType: StatusIconType };
  componentShouldReplaceItem?: boolean;
}

export default class QaStatus extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare snackbar: Snackbar;
  @service declare session: Session;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare permissionsService: any;

  @cached
  get qaStatusAttribute() {
    return this.args.conversationEntry.getAttributeForDescriptor('qa_status');
  }

  get status(): QaStatusValue | undefined {
    // Prefer pre-computed statusValue arg from table-row for Glimmer reactivity
    // (primitive arg change triggers re-render, unlike deep object mutations)
    if (this.args.statusValue !== undefined) {
      return this.args.statusValue as QaStatusValue;
    }
    if (this.inboxSidebarService.currentMonitorId) {
      return this.args.conversationEntry.getQaStatusForMonitor(
        this.inboxSidebarService.currentMonitorId,
      ) as QaStatusValue | undefined;
    }
    return this.qaStatusAttribute?.value as QaStatusValue | undefined;
  }

  get displayText(): string {
    let status = this.status;

    if (!status) {
      return this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed');
    }

    switch (status) {
      case QaStatusValue.NeedsFix:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix');
      case QaStatusValue.WontFix:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix');
      case QaStatusValue.FixComplete:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete');
      case QaStatusValue.PassedReview:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review');
      default:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed');
    }
  }

  get iconType(): StatusIconType {
    return STATUS_ICON_TYPES[this.status ?? QaStatusValue.Unreviewed];
  }

  get colorClasses(): string {
    return 'border border-neutral-border text-default';
  }

  // TODO: Replace IcSelect + __separator__ workaround with IcDropdownOpener @groupList
  // once IcDropdownOpener is fixed to close on router transitions.
  // See: https://github.com/intercom/embercom/pull/118243#discussion_r2932827661
  get selectItems(): SelectItem[] {
    let component = QaStatusSelectItem;

    return [
      {
        value: QaStatusValue.Unreviewed,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed'),
        component,
        componentAttrs: { iconType: STATUS_ICON_TYPES[QaStatusValue.Unreviewed] },
      },
      {
        value: QaStatusValue.PassedReview,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review'),
        component,
        componentAttrs: { iconType: STATUS_ICON_TYPES[QaStatusValue.PassedReview] },
      },
      {
        value: '__separator__' as const,
        text: '',
        component: QaStatusSeparator,
        componentShouldReplaceItem: true,
      },
      {
        value: QaStatusValue.NeedsFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix'),
        component,
        componentAttrs: { iconType: STATUS_ICON_TYPES[QaStatusValue.NeedsFix] },
      },
      {
        value: QaStatusValue.WontFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix'),
        component,
        componentAttrs: { iconType: STATUS_ICON_TYPES[QaStatusValue.WontFix] },
      },
      {
        value: QaStatusValue.FixComplete,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete'),
        component,
        componentAttrs: { iconType: STATUS_ICON_TYPES[QaStatusValue.FixComplete] },
      },
    ];
  }

  get selectedValue(): QaStatusValue {
    return this.status ?? QaStatusValue.Unreviewed;
  }

  @action
  async handleSelectItem(selectedValue: QaStatusValue | '__separator__'): Promise<void> {
    if (selectedValue === '__separator__') {
      return;
    }

    let conversationId = this.args.conversationEntry.id;
    let appId = this.session.workspace.id;
    let apiValue = selectedValue === QaStatusValue.Unreviewed ? null : selectedValue;
    let monitorId = this.inboxSidebarService.currentMonitorId;

    if (monitorId) {
      let monitorAttr =
        this.args.conversationEntry.getAttributeForDescriptor('qa_monitor_statuses');
      let previousValue = monitorAttr?.value;

      try {
        if (monitorAttr) {
          monitorAttr.value = setValueForMonitorPairs(
            typeof previousValue === 'string' ? previousValue : null,
            monitorId,
            apiValue,
          );
          this.bumpAttributesReference();
        }

        await putRequest(
          `/teammate_app/qa/conversations/${conversationId}/monitor_attribute?app_id=${appId}`,
          {
            monitor_id: monitorId,
            attribute_type: 'status',
            value: apiValue,
            client_assigned_uuid: generateUUID(),
            app_id: appId,
          },
        );

        this.reactTeammateAppIsland.dispatchTeammateAppEvent('qa-attributes:updated', {
          conversationId,
        });
      } catch (e: unknown) {
        if (monitorAttr) {
          monitorAttr.value = previousValue;
        }
        this.bumpAttributesReference();
        return this.handleUpdateError(e);
      }
    } else {
      // "Any monitor" mode: find monitors matching the current status filter and update each
      let currentFilter = this.inboxSidebarService.currentQaStatusFilter;
      let matchingMonitorIds: string[] = [];

      if (currentFilter) {
        let monitorIdsAttr =
          this.args.conversationEntry.getAttributeForDescriptor('qa_monitor_ids');
        let monitorIdsValue =
          typeof monitorIdsAttr?.value === 'string' ? monitorIdsAttr.value : null;
        let allMonitorIds = monitorIdsValue ? monitorIdsValue.split('\n').filter(Boolean) : [];

        let monitorStatusesAttr =
          this.args.conversationEntry.getAttributeForDescriptor('qa_monitor_statuses');
        let pairsValue =
          typeof monitorStatusesAttr?.value === 'string' ? monitorStatusesAttr.value : '';

        matchingMonitorIds = allMonitorIds.filter((id) => {
          let status = getValueFromMonitorPairs(pairsValue, id);
          return statusMatchesFilter(status, currentFilter);
        });
      }

      if (matchingMonitorIds.length > 0) {
        // Update per-monitor statuses for all matching monitors
        let monitorAttr =
          this.args.conversationEntry.getAttributeForDescriptor('qa_monitor_statuses');
        let previousValue = monitorAttr?.value;

        try {
          if (monitorAttr) {
            let updatedPairs = typeof previousValue === 'string' ? previousValue : '';
            for (let mid of matchingMonitorIds) {
              updatedPairs = setValueForMonitorPairs(updatedPairs || null, mid, apiValue);
            }
            monitorAttr.value = updatedPairs;
            this.bumpAttributesReference();
          }

          await Promise.all(
            matchingMonitorIds.map((mid) =>
              putRequest(
                `/teammate_app/qa/conversations/${conversationId}/monitor_attribute?app_id=${appId}`,
                {
                  monitor_id: mid,
                  attribute_type: 'status',
                  value: apiValue,
                  client_assigned_uuid: generateUUID(),
                  app_id: appId,
                },
              ),
            ),
          );

          this.reactTeammateAppIsland.dispatchTeammateAppEvent('qa-attributes:updated', {
            conversationId,
          });
        } catch (e: unknown) {
          if (monitorAttr) {
            monitorAttr.value = previousValue;
          }
          this.bumpAttributesReference();
          return this.handleUpdateError(e);
        }
      } else {
        // No monitor context: update global qa_status
        let attr = this.qaStatusAttribute;
        let previousValue = attr?.value;

        try {
          if (attr) {
            attr.value = selectedValue;
          }

          await putRequest(
            `/ember/conversations/${conversationId}/conversation_attributes/qa_status?app_id=${appId}`,
            { value: apiValue },
          );

          this.reactTeammateAppIsland.dispatchTeammateAppEvent('qa-attributes:updated', {
            conversationId,
          });
        } catch (e: unknown) {
          if (attr) {
            attr.value = previousValue;
          }
          return this.handleUpdateError(e);
        }
      }
    }
  }

  private bumpAttributesReference() {
    if (this.args.conversationEntry.attributes) {
      this.args.conversationEntry.attributes = [...this.args.conversationEntry.attributes];
    }
  }

  private async handleUpdateError(e: unknown) {
    if (e instanceof ResponseError && e.response.status === 403) {
      let json = await e.response.json();
      if (json?.required_permission) {
        this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
          json.required_permission,
        );
        return;
      }
    }
    this.snackbar.notifyError(this.intl.t('inbox.conversations-table.errors.qa-status-update'));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationsTable::TableCells::QaStatus': typeof QaStatus;
    'inbox2/conversations-table/table-cells/qa-status': typeof QaStatus;
  }
}
